import * as React from "react";
import { useMe } from "./useMe.js";
import { useToastOptional } from "../components/ToastProvider.js";

/**
 * Daily-streak milestone celebration (TEX-2237).
 *
 * Fires a Field Day toast EXACTLY ONCE when the player's day streak crosses a
 * milestone. Mount this ONCE at the app root (inside <ToastProvider>) — every
 * useMe consumer shares the ['me'] cache, so a single mounted effect is what
 * prevents double-firing. Fire-once persistence is a per-identity localStorage
 * high-water mark; it resets when the streak breaks so rebuilding re-celebrates.
 */
const MILESTONES = [3, 7, 14, 30, 50, 100, 200, 365];
const KEY_PREFIX = "sa_daily_streak_celebrated:";

function readMark(key: string): number {
  try {
    return Number(localStorage.getItem(key) ?? 0) || 0;
  } catch {
    return 0;
  }
}

function writeMark(key: string, value: number): void {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    /* private mode / storage disabled — skip persistence, no-op */
  }
}

export function useDailyStreakCelebration(): void {
  const { data: me } = useMe();
  const toast = useToastOptional();
  const streak = me?.stats?.daily_streak ?? 0;
  const identityKey = me?.user?.id ?? me?.session_id ?? null;

  React.useEffect(() => {
    if (!toast || !identityKey) return;
    const key = KEY_PREFIX + identityKey;
    const last = readMark(key);

    // Streak broke — reset the high-water mark so the next rebuild re-celebrates.
    if (streak === 0) {
      if (last !== 0) writeMark(key, 0);
      return;
    }

    const milestone = MILESTONES.filter((m) => m <= streak).pop() ?? 0;
    if (milestone > last) {
      toast.show({
        eyebrow: "Streak!",
        title: `${milestone}-day streak`,
        body: "You showed up again. Keep the fire going — play tomorrow too!",
        sticker: "🔥",
        duration: 7000,
      });
      writeMark(key, milestone);
    }
  }, [toast, identityKey, streak]);
}

/** Mount-once wrapper for the celebration effect. Renders nothing. */
export function StreakCelebrations(): null {
  useDailyStreakCelebration();
  return null;
}
